// HFLedger/CurrencyConverter.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CurrencyConverter.css';

function CurrencyConverter({ user, language, SERVER_ROOT, settings }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [rates, setRates] = useState({});
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // 환율 변환 폼
  const [convertForm, setConvertForm] = useState({
    amount: 1000,
    fromCurrency: settings.default_currency,
    toCurrency: settings.default_currency === 'KRW' ? 'USD' : 'KRW'
  });
  
  // 변환 결과
  const [convertResult, setConvertResult] = useState(null);

  // 데이터 불러오기
  useEffect(() => {
    const fetchCurrencyData = async () => {
      setLoading(true);
      try {
        // 1. 지원 통화 목록
        const currenciesResponse = await axios.get(`${SERVER_ROOT}/api/hfl/currency/supported`);
        setCurrencies(currenciesResponse.data);
        
        // 2. 환율 정보
        const ratesResponse = await axios.get(`${SERVER_ROOT}/api/hfl/currency/rates?base_currency=${settings.default_currency}`);
        
        // 환율 데이터 처리
        const ratesData = {};
        ratesResponse.data.forEach(item => {
          ratesData[item.target_currency] = parseFloat(item.rate);
        });
        
        setRates(ratesData);
        
        // 마지막 업데이트 날짜
        if (ratesResponse.data.length > 0) {
          setLastUpdate(new Date(ratesResponse.data[0].effective_date));
        }
        
    } catch (err) {
        console.error('Error fetching currency data:', err);
        setError(err.message || '환율 정보를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCurrencyData();
  }, [SERVER_ROOT, settings.default_currency]);

  // 환율 업데이트 핸들러
  const handleUpdateRates = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${SERVER_ROOT}/api/hfl/currency/rates/update`);
      
      // 업데이트 후 환율 다시 불러오기
      const ratesResponse = await axios.get(`${SERVER_ROOT}/api/hfl/currency/rates?base_currency=${settings.default_currency}`);
      
      // 환율 데이터 처리
      const ratesData = {};
      ratesResponse.data.forEach(item => {
        ratesData[item.target_currency] = parseFloat(item.rate);
      });
      
      setRates(ratesData);
      
      // 마지막 업데이트 날짜
      if (ratesResponse.data.length > 0) {
        setLastUpdate(new Date(ratesResponse.data[0].effective_date));
      }
      
      alert(language === 'KR' 
        ? '환율이 성공적으로 업데이트되었습니다.' 
        : 'Currency rates updated successfully.');
      
    } catch (err) {
      console.error('Error updating rates:', err);
      setError(err.message || (language === 'KR' 
        ? '환율 업데이트 중 오류가 발생했습니다.' 
        : 'Error updating currency rates.'));
    } finally {
      setLoading(false);
    }
  };

  // 입력 변경 핸들러
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setConvertForm(prev => ({
      ...prev,
      [name]: name === 'amount' ? parseFloat(value) || 0 : value
    }));
  };

  // 통화 스왑 핸들러
  const handleSwapCurrencies = () => {
    setConvertForm(prev => ({
      ...prev,
      fromCurrency: prev.toCurrency,
      toCurrency: prev.fromCurrency
    }));
  };

  // 환율 변환 계산
  const handleConvert = async (e) => {
    e?.preventDefault();
    
    const { amount, fromCurrency, toCurrency } = convertForm;
    
    if (!amount || amount <= 0) {
      setError(language === 'KR' 
        ? '유효한 금액을 입력해주세요.' 
        : 'Please enter a valid amount.');
      return;
    }
    
    try {
      // API를 통한 변환
      const response = await axios.get(
        `${SERVER_ROOT}/api/hfl/currency/convert?from_currency=${fromCurrency}&to_currency=${toCurrency}&amount=${amount}`
      );
      
      setConvertResult(response.data);
      setError(null);
      
    } catch (err) {
      console.error('Error converting currency:', err);
      setError(err.message || (language === 'KR' 
        ? '환율 변환 중 오류가 발생했습니다.' 
        : 'Error converting currency.'));
    }
  };

  // 페이지 로드시 초기 변환 실행
  useEffect(() => {
    if (Object.keys(rates).length > 0) {
      handleConvert();
    }
  }, [rates]);

  // 통화 포맷팅
  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat(language === 'KR' ? 'ko-KR' : 'en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // 날짜 포맷팅
  const formatDate = (date) => {
    if (!date) return '';
    
    return new Intl.DateTimeFormat(language === 'KR' ? 'ko-KR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  // 통화 심볼 찾기
  const getCurrencySymbol = (code) => {
    const currency = currencies.find(c => c.code === code);
    return currency ? currency.symbol : code;
  };

  // 주요 통화 환율표 생성
  const createExchangeRateTable = () => {
    // 주요 통화 목록
    const mainCurrencies = ['USD', 'EUR', 'JPY', 'GBP', 'CNY', 'KRW'].filter(c => c !== settings.default_currency);
    
    return (
      <div className="exchange-rate-table">
        <h4>{language === 'KR' ? '주요 환율' : 'Main Exchange Rates'}</h4>
        <div className="rate-table-header">
          <span>{language === 'KR' ? '통화' : 'Currency'}</span>
          <span>1 {settings.default_currency}</span>
          <span>1 {language === 'KR' ? '외화' : 'Foreign'}</span>
        </div>
        <div className="rate-table-body">
          {mainCurrencies.map(currency => {
            const rate = rates[currency];
            const inverseRate = 1 / rate;
            
            return (
              <div key={currency} className="rate-table-row">
                <span className="currency-code">
                  <span className="currency-flag">{getCurrencySymbol(currency)}</span>
                  {currency}
                </span>
                <span>{rate ? rate.toFixed(4) : '-'}</span>
                <span>{inverseRate ? inverseRate.toFixed(4) : '-'}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading && Object.keys(rates).length === 0) {
    return (
      <div className="converter-loading">
        <div className="converter-spinner"></div>
        <p>{language === 'KR' ? '환율 정보 로딩 중...' : 'Loading currency data...'}</p>
      </div>
    );
  }

  return (
    <div className="currency-converter-container">
      <div className="converter-header">
        <h3>{language === 'KR' ? '통화 변환기' : 'Currency Converter'}</h3>
        <div className="last-update">
          {lastUpdate && (
            <span>
              {language === 'KR' ? '마지막 업데이트: ' : 'Last Updated: '}
              {formatDate(lastUpdate)}
            </span>
          )}
          <button 
            className="update-rates-button"
            onClick={handleUpdateRates}
            disabled={loading}
          >
            <i className="fas fa-sync-alt"></i>
            {language === 'KR' ? '환율 업데이트' : 'Update Rates'}
          </button>
        </div>
      </div>
      
      {/* 환율 변환 폼 */}
      <div className="converter-form-container">
        <form onSubmit={handleConvert} className="converter-form">
          {/* 금액 입력 */}
          <div className="form-group amount-input">
            <label htmlFor="amount">{language === 'KR' ? '금액' : 'Amount'}</label>
            <input 
              type="number" 
              id="amount"
              name="amount"
              value={convertForm.amount}
              onChange={handleInputChange}
              min="0.01"
              step="0.01"
              required
            />
          </div>
          
          {/* From 통화 */}
          <div className="form-group currency-select">
            <label htmlFor="fromCurrency">{language === 'KR' ? '변환 전 통화' : 'From'}</label>
            <select 
              id="fromCurrency"
              name="fromCurrency"
              value={convertForm.fromCurrency}
              onChange={handleInputChange}
            >
              {currencies.map(currency => (
                <option key={`from-${currency.code}`} value={currency.code}>
                  {currency.code} - {currency.name} ({currency.symbol})
                </option>
              ))}
            </select>
          </div>
          
          {/* 스왑 버튼 */}
          <button 
            type="button"
            className="swap-button"
            onClick={handleSwapCurrencies}
          >
            <i className="fas fa-exchange-alt"></i>
          </button>
          
          {/* To 통화 */}
          <div className="form-group currency-select">
            <label htmlFor="toCurrency">{language === 'KR' ? '변환 후 통화' : 'To'}</label>
            <select 
              id="toCurrency"
              name="toCurrency"
              value={convertForm.toCurrency}
              onChange={handleInputChange}
            >
              {currencies.map(currency => (
                <option key={`to-${currency.code}`} value={currency.code}>
                  {currency.code} - {currency.name} ({currency.symbol})
                </option>
              ))}
            </select>
          </div>
          
          {/* 변환 버튼 */}
          <button 
            type="submit" 
            className="convert-button"
            disabled={loading}
          >
            {loading ? (
              <span className="loading-spinner-small"></span>
            ) : (
              <>{language === 'KR' ? '변환' : 'Convert'}</>
            )}
          </button>
        </form>
        
        {/* 에러 메시지 */}
        {error && (
          <div className="converter-error">
            {error}
          </div>
        )}
        
        {/* 변환 결과 */}
        {convertResult && (
          <div className="conversion-result">
            <h4>{language === 'KR' ? '변환 결과' : 'Conversion Result'}</h4>
            <div className="result-amount">
              <span className="from-amount">
                {formatCurrency(convertResult.amount, convertResult.from_currency)}
              </span>
              <span className="equal-sign">=</span>
              <span className="to-amount">
                {formatCurrency(convertResult.converted_amount, convertResult.to_currency)}
              </span>
            </div>
            <div className="exchange-rate-info">
              <p>
                {language === 'KR' 
                  ? `1 ${convertResult.from_currency} = ${convertResult.rate.toFixed(4)} ${convertResult.to_currency}`
                  : `1 ${convertResult.from_currency} = ${convertResult.rate.toFixed(4)} ${convertResult.to_currency}`}
              </p>
              <p>
                {language === 'KR' 
                  ? `1 ${convertResult.to_currency} = ${(1 / convertResult.rate).toFixed(4)} ${convertResult.from_currency}`
                  : `1 ${convertResult.to_currency} = ${(1 / convertResult.rate).toFixed(4)} ${convertResult.from_currency}`}
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* 환율표 */}
      {createExchangeRateTable()}
      
      {/* 참고 정보 */}
      <div className="converter-notes">
        <p>
          <i className="fas fa-info-circle"></i>
          {language === 'KR' 
            ? '참고: 환율은 매일 업데이트되며, 실제 환전 시 적용되는 환율과 다를 수 있습니다.'
            : 'Note: Exchange rates are updated daily and may differ from rates applied in actual currency exchange.'}
        </p>
      </div>
    </div>
  );
}

export default CurrencyConverter;